import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { AxiosError } from 'axios'

import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'
import { useTflViewerStore } from '@/features/view-tfl-uploaded/store'

import { useDeleteDocument, useDocumentStore } from '..'

const DeleteDocumentDialog: React.FC = () => {
  const { t } = useTranslation()
  // Get delete state from store
  const {
    openDocuments,
    isDeleteMultiple,
    deleteMultipleDocumentIds,
    deleteDialogOpen,
    setDeleteDialogOpen,
    clearDeletingDocument,
    setOpenDocument,
    setActiveDocumentId,
  } = useDocumentStore()

  const { metadataByDocId, removeDocumentMetadata } = useTflViewerStore()

  // Delete mutation
  const { reset, mutateAsync, isPending } = useDeleteDocument()

  const handleOpenChange = (newOpen: boolean) => {
    setDeleteDialogOpen(newOpen)
    if (!newOpen) {
      if (!isDeleteMultiple) clearDeletingDocument()
      reset()
    }
  }

  const handleConfirm = async () => {
    if (deleteMultipleDocumentIds.length === 0) return

    const results = await Promise.allSettled(
      deleteMultipleDocumentIds.map((id, idx) =>
        mutateAsync({
          documentId: id,
          // Refetch queries only on the last document to avoid multiple refetches
          refetchQueries: idx === deleteMultipleDocumentIds.length - 1,
        }).catch((error: unknown) => {
          // Extract error message from axios error response
          const axiosError = error as AxiosError<{ message?: string }>
          const errorMessage =
            axiosError.response?.data?.message ||
            axiosError.message ||
            t('document.dialog.messages.errorGeneric')

          // Show error toast
          triggerToast('error', errorMessage)
          // Re-throw to mark as rejected in Promise.allSettled
          throw error
        })
      )
    )

    // Remove deleted documents from open documents
    const openIdx = openDocuments.findIndex((document) =>
      deleteMultipleDocumentIds.includes(document.id)
    )

    setOpenDocument(
      openDocuments.filter(
        (document) => !deleteMultipleDocumentIds.includes(document.id)
      )
    )
    setActiveDocumentId(openDocuments[openIdx - 1]?.id || null)

    // Remove deleted documents from metadata
    removeDocumentMetadata(deleteMultipleDocumentIds)

    // Only show success if at least one deletion succeeded
    const hasSuccess = results.some((result) => result.status === 'fulfilled')
    if (hasSuccess) {
      triggerToast('success', t('document.messages.deleteSuccess'))
    }
    setDeleteDialogOpen(false)
    clearDeletingDocument()
  }

  const isDeleteAll = useMemo(
    () =>
      deleteMultipleDocumentIds.length ===
      Object.values(metadataByDocId).filter(Boolean).length,
    [deleteMultipleDocumentIds, metadataByDocId]
  )
  let dialogTitle: string
  if (isDeleteAll) {
    dialogTitle = t('document.dialog.deleteAllTitle')
  } else if (isDeleteMultiple) {
    dialogTitle = t('document.dialog.deleteMultipleTitle')
  } else {
    dialogTitle = t('document.dialog.deleteTitle')
  }

  let dialogDescription: string
  if (isDeleteAll) {
    dialogDescription = t('document.dialog.deleteAllDescription')
  } else if (isDeleteMultiple) {
    dialogDescription = t('document.dialog.deleteMultipleDescription')
  } else {
    dialogDescription = t('document.dialog.deleteDescription')
  }

  return (
    <Dialog open={deleteDialogOpen} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="sm:max-w-[480px] bg-white shadow-xl rounded-2xl p-6">
          <DialogHeader className="space-y-0.5">
            <DialogTitle className="m-0 font-semibold text-md text-primary-900">
              {dialogTitle}
            </DialogTitle>
            <DialogDescription className="text-sm text-tertiary-600">
              {dialogDescription}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex justify-stretch !space-x-0 gap-lg pt-3">
            <Button
              type="button"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid bg-white"
              variant="default"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid border-transparent !text-white !bg-error-solid"
              variant="default"
              onClick={handleConfirm}
              disabled={isPending}
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export default DeleteDocumentDialog
